import { useState } from "react";

import { normalizeToDate } from "common/utils/dates";
import { Feature, SubscriptionFrequency, UserSubscriptionAction } from "common/types";

import Alert from "client/common/components/content/Alert";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { DetailSubscription } from "client/subscriptions/types";
import { useSubscriptionQuery, useUpdateSubscriptionMutation } from "client/subscriptions/hooks";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useFeatures } from "client/clinics/hooks";

import SubscriptionFrequencySelect from "../SubscriptionFrequencySelect";
import SubscriptionDateBanners from "../SubscriptionDateBanners";
import { doesSubscriptionRequireAction, getRetentionOfferDeadlineWarning } from "../../utils";
import SubscriptionAlert from "../SubscriptionAlert";

export type SubscriptionFrequencyUpdateDrawerProps = {
  id: number;
};

type SubscriptionFrequencyUpdateFormProps = {
  subscription: DetailSubscription;
};

const SubscriptionFrequencyUpdateForm = ({
  subscription,
}: SubscriptionFrequencyUpdateFormProps) => {
  const { close } = useCurrentDrawer();
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];
  const {
    mutate: update,
    isPending: isLoading,
    error,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  const [value, setValue] = useState<SubscriptionFrequency>(
    subscription.is_active_frequency_valid
      ? subscription.active_frequency
      : subscription.recommended_frequency,
  );

  const submit = () => {
    update({
      action: UserSubscriptionAction.Modify,
      ...value,
    });
  };

  const selectedFrequency = [
    subscription.active_frequency,
    subscription.recommended_frequency,
    ...subscription.alternative_frequencies,
  ].find(
    (frequency) =>
      SubscriptionFrequencySelect.frequencyValueToString(frequency) ===
      SubscriptionFrequencySelect.frequencyValueToString(value),
  );

  const retentionOfferDeadlineWarning = getRetentionOfferDeadlineWarning({
    offer: subscription.accepted_retention_offer,
    scheduleDate: isEdbEnabled
      ? selectedFrequency?.expected_delivery_dates[0]
      : selectedFrequency?.next_order_dates[0],
    isEdbEnabled,
  });

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Update frequency"
        description={`Currently receiving ${subscription.active_frequency.display.toLowerCase()}.`}
        onClose={close}
      />
      <DrawerLayout.Form
        size="l"
        submitText="Update frequency"
        canSubmit={!doesSubscriptionRequireAction(subscription)}
        onSubmit={submit}
        error={error}
        isLoading={isLoading}
        fieldErrors={[error?.errors.action, error?.errors.frequency, error?.errors.interval_unit]}
      >
        {doesSubscriptionRequireAction(subscription) && (
          <DrawerLayout.Alerts>
            <SubscriptionAlert subscription={subscription} />
          </DrawerLayout.Alerts>
        )}
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>Select your new Autoship frequency:</DrawerLayout.Form.Label>
          <SubscriptionFrequencySelect
            subscription={subscription}
            value={value}
            onSelect={setValue}
          />
        </DrawerLayout.Form.Group>
        {selectedFrequency && (
          <DrawerLayout.Form.Group>
            <DrawerLayout.Form.Label>
              {isEdbEnabled
                ? "Your next estimated delivery dates will be:"
                : "Your next order dates will be:"}
            </DrawerLayout.Form.Label>
            <SubscriptionDateBanners
              dates={(isEdbEnabled
                ? selectedFrequency.expected_delivery_dates
                : selectedFrequency.next_order_dates
              ).map((str) => normalizeToDate(str))}
            />
            {retentionOfferDeadlineWarning && (
              <Alert variant="warning">{retentionOfferDeadlineWarning}</Alert>
            )}
          </DrawerLayout.Form.Group>
        )}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

const SubscriptionFrequencyUpdateDrawer = ({ id }: SubscriptionFrequencyUpdateDrawerProps) => {
  const { data: subscription, isLoading, error } = useSubscriptionQuery(id);

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!subscription || error) {
    return <DrawerLayout.FetchError entityName="Autoship" error={error} />;
  }

  return <SubscriptionFrequencyUpdateForm subscription={subscription} />;
};

export default SubscriptionFrequencyUpdateDrawer;
